import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { UserSvgIcon, ReservationSvgIcon } from "../../icons/SvgIcons";
import Tooltip from "../ToolTipWrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPowerOff } from "@fortawesome/free-solid-svg-icons";
import { labelColors, iconStatusMap, statusGroups } from "./../DefaultImports";

// === Styled Components ===

const TableWrapper = styled.div`
  background-color: #fff;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
  overflow: hidden;
  width: 310px;
  height: 80px;
`;

const StyledTable = styled.table`
  min-width: 101%;
  height: 105%;
  border-collapse: collapse;
  font-size: 0.72rem;
  border-spacing: 0;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const Th = styled.th`
  border: 1px solid #d0dad9;
  color: #2e3e3b;
  background-color: ${({ color }) =>
    color
      ? `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(
          color.slice(3, 5),
          16
        )}, ${parseInt(color.slice(5, 7), 16)}, 0.7)`
      : "rgba(255, 255, 255, 0.8)"};
  text-align: center;
  font-weight: 500;
  white-space: nowrap;
  font-size: 11px;
  vertical-align: middle;
  padding-top: 4px;
  width: 20px;
`;

const Td = styled.td`
  border: 1px solid #d0dad9;
  text-align: center;
  min-width: 28px;
  height: 20px;
  line-height: 1;
  padding: 1px 2px;
  font-size: 0.7rem;
  font-weight: 600 !important;
  font-family: "Roboto", sans-serif;
  cursor: pointer;
  background-color: ${({ isSelected }) => (isSelected ? "#b6ccc8" : "#ffffff")};
  color: ${({ isSelected }) => (isSelected ? "#ffffff" : "#99b0ad")};
  transition: background-color 0.15s ease, transform 0.1s ease;

  &:hover {
    background-color: ${({ isSelected }) =>
      isSelected ? "#a1bab6" : "#eef2f1"};
    transform: scale(1.02);
  }
`;

const RowLabel = styled.td`
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 11px;
  height: 22px;
  width: 15px;
`;

const IconSpan = styled.span`
  color: #6b8c88;
  font-size: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const BottomRightTd = styled(Td)`
  border-bottom-right-radius: 0px;
`;

// === Main Component ===

function SelectableStatusOptions({
  onSelectChange,
  selectedItems,
  reservations,
}) {
  const [statusSummary, setStatusSummary] = useState({});

  const allDisplayLabels = [
    "A CHEGAR",
    "À PORTA",
    "ATRASADA",
    "REST",
    "CLIENTE",
    "FINALIZADA",
    "CANCELADAS",
    "Mesa livre",
  ];

  useEffect(() => {
    if (reservations) {
      const summary = {};
      allDisplayLabels.forEach((label) => {
        summary[label] = { people: 0, reservations: 0 };
      });

      reservations.forEach((res) => {
        for (const [label, statuses] of Object.entries(statusGroups)) {
          if (statuses.includes(res.status)) {
            summary[label].people += res.number_of_people || 0;
            summary[label].reservations += 1;
          }
        }
      });

      setStatusSummary(summary);
    }
  }, [reservations]);

  const areAllSelected = allDisplayLabels
    .filter((label) => label !== "CANCELADAS")
    .every((label) => selectedItems.includes(label));

  const toggleSelection = (label) => {
    if (selectedItems.includes(label)) {
      onSelectChange(selectedItems.filter((l) => l !== label));
    } else {
      onSelectChange([...selectedItems, label]);
    }
  };

  const toggleAll = () => {
    if (areAllSelected) {
      onSelectChange([]);
    } else {
      const labelsWithoutCancelled = allDisplayLabels.filter(
        (label) => label !== "CANCELADAS"
      );
      onSelectChange([...labelsWithoutCancelled]);
    }
  };

  // ✅ NEW: totals depend on which TH/columns are selected (excluding CANCELADAS)
  const calculateSelectedTotal = (field) => {
    const selectableLabels = allDisplayLabels.filter(
      (label) => label !== "CANCELADAS"
    );

    // only sum selected columns
    const labelsToSum = selectableLabels.filter((label) =>
      selectedItems.includes(label)
    );

    return labelsToSum.reduce(
      (acc, label) => acc + (statusSummary[label]?.[field] || 0),
      0
    );
  };

  return (
    <TableWrapper>
      <StyledTable>
        <thead>
          <tr>
            <Th>
              <Tooltip
                text={
                  areAllSelected ? "Desselecionar todos" : "Selecionar todos"
                }
              >
                <IconSpan onClick={toggleAll} style={{ cursor: "pointer" }}>
                  <FontAwesomeIcon
                    icon={faPowerOff}
                    style={{ color: "#99B0AD" }}
                  />
                </IconSpan>
              </Tooltip>
            </Th>

            {allDisplayLabels
              .filter((label) => label !== "CANCELADAS")
              .map((label) => (
                <Th key={label}>
                  <Tooltip text={iconStatusMap[label].label}>
                    <IconSpan>
                      <FontAwesomeIcon
                        icon={iconStatusMap[label].icon}
                        style={{ color: labelColors[label] }}
                      />
                    </IconSpan>
                  </Tooltip>
                </Th>
              ))}

            <Th>
              <Tooltip text={iconStatusMap["TOTAL"].label}>
                <IconSpan>
                  <FontAwesomeIcon icon={iconStatusMap["TOTAL"].icon} />
                </IconSpan>
              </Tooltip>
            </Th>

            <Th color={labelColors["CANCELADAS"]}>
              <Tooltip text={iconStatusMap["CANCELADAS"].label}>
                <IconSpan>
                  <FontAwesomeIcon
                    icon={iconStatusMap["CANCELADAS"].icon}
                    style={{ color: "white", fontSize: "13px" }}
                  />
                </IconSpan>
              </Tooltip>
            </Th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <RowLabel>
              <Tooltip text="Nº de clientes">
                <UserSvgIcon
                  width={12}
                  height={12}
                  strokeColor={"#2e3e3b"}
                  style={{ minWidth: "12px" }}
                />
              </Tooltip>
            </RowLabel>

            {allDisplayLabels
              .filter((label) => label !== "CANCELADAS")
              .map((label) => (
                <Td
                  key={`people-${label}`}
                  isSelected={selectedItems.includes(label)}
                  onClick={() => toggleSelection(label)}
                >
                  {statusSummary[label]?.people || 0}
                </Td>
              ))}

            {/* ✅ TOTAL now depends on selected columns */}
            <Td isSelected={false}>{calculateSelectedTotal("people")}</Td>

            <Td
              isSelected={selectedItems.includes("CANCELADAS")}
              onClick={() => toggleSelection("CANCELADAS")}
            >
              {statusSummary["CANCELADAS"]?.people || 0}
            </Td>
          </tr>

          <tr>
            <RowLabel>
              <Tooltip text="Nº de reservas">
                <ReservationSvgIcon
                  width={12}
                  height={12}
                  strokeColor={"#2e3e3b"}
                  style={{ minWidth: "12px" }}
                />
              </Tooltip>
            </RowLabel>

            {allDisplayLabels
              .filter((label) => label !== "CANCELADAS")
              .map((label) => (
                <Td
                  key={`res-${label}`}
                  isSelected={selectedItems.includes(label)}
                  onClick={() => toggleSelection(label)}
                >
                  {statusSummary[label]?.reservations || 0}
                </Td>
              ))}

            {/* ✅ TOTAL now depends on selected columns */}
            <Td isSelected={false}>{calculateSelectedTotal("reservations")}</Td>

            <BottomRightTd
              isSelected={selectedItems.includes("CANCELADAS")}
              onClick={() => toggleSelection("CANCELADAS")}
            >
              {statusSummary["CANCELADAS"]?.reservations || 0}
            </BottomRightTd>
          </tr>
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
}

export default SelectableStatusOptions;
